/**
 * Which charts a catalogue report shows, and how its rows become chart data. Every chart answers the report's question
 * with the report's own figures (GET /reports/{key}) or its monthly headline figures (GET /reports/{key}/trend) — never
 * invented data. Reports not listed here show no chart (a list of records reads better as a table).
 */
import type { Schema } from '@dealdesk/api-schema';

import type { ChartDatum, ChartSeries, ChartTone, ChartValueKind } from '@/business';

type Result = Schema<'ReportResult'>;
type Column = Result['columns'][number];

export interface RowsChart {
  type: 'rows';
  /** i18n key under `charts.titles`. */
  title: string;
  form: 'bars' | 'stacked';
  horizontal?: boolean;
  /** Category column(s): the first non-empty cell names the row. */
  category: readonly string[];
  series: readonly { col: string; tone: ChartTone }[];
  /** At most this many rows (largest first by the first series). */
  limit?: number;
}

/** Part-to-whole of a status column: rows counted per value (at most five values). */
export interface CountChart {
  type: 'count';
  title: string;
  category: string;
  tones: Readonly<Record<string, ChartTone>>;
}

/** Monthly headline figures of the report (GET /reports/{key}/trend). */
export interface TrendSpec {
  type: 'trend';
  title: string;
  kind: 'money' | 'integer' | 'days';
  /** Figure keys left out (counts of banks, entries…). */
  exclude?: readonly string[];
}

export type ChartSpec = RowsChart | CountChart | TrendSpec;

const AGE5: ChartTone[] = ['neutralSoft', 'neutral', 'attentionSoft', 'attention', 'negative'];
const age = (cols: readonly string[]) => cols.map((col, i) => ({ col, tone: AGE5[cols.length === 4 ? [0, 2, 3, 4][i] ?? 4 : i] ?? 'negative' }));
const bank = ['bank'] as const;

export const REPORT_CHARTS: Readonly<Record<string, readonly ChartSpec[]>> = {
  // Operational (snapshots): workload, ageing and SLA.
  'pending-with': [{ type: 'rows', title: 'pendingWith', form: 'stacked', horizontal: true, category: ['party', 'type'], series: age(['d0_2', 'd3_5', 'd6_10', 'd10p']), limit: 10 }],
  'aging-by-stage': [{ type: 'rows', title: 'agingByStage', form: 'stacked', horizontal: true, category: ['stage'], series: age(['d0_7', 'd8_14', 'd15_30', 'd31_60', 'd60p']) }],
  sla: [{ type: 'rows', title: 'sla', form: 'stacked', horizontal: true, category: ['step'], series: [{ col: 'breached', tone: 'negative' }, { col: 'atRisk', tone: 'attention' }, { col: 'onTrack', tone: 'positive' }, { col: 'paused', tone: 'neutralSoft' }], limit: 10 }],
  'bank-queries': [{ type: 'rows', title: 'bankQueries', form: 'bars', horizontal: true, category: bank, series: [{ col: 'open', tone: 'progress' }, { col: 'over5', tone: 'attention' }, { col: 'overdue', tone: 'negative' }] }],
  holds: [{ type: 'count', title: 'holds', category: 'state', tones: { on_hold: 'attention', resumes_today: 'progress', overdue: 'negative' } }],
  'liability-letters': [{ type: 'count', title: 'liabilityLetters', category: 'state', tones: { required: 'neutralSoft', requested: 'progress', reissue_requested: 'progress', available: 'positive', used: 'positiveStrong', expiring: 'attention', expired: 'negative', not_required: 'neutral' } }],
  'employer-enlistment': [{ type: 'count', title: 'enlistment', category: 'status', tones: { requested: 'neutralSoft', documents_pending: 'attention', submitted_to_bank: 'progress', enlisted: 'positive', declined: 'negative' } }],
  'closed-reasons': [{ type: 'rows', title: 'closedReasons', form: 'stacked', horizontal: true, category: ['reason'], series: [{ col: 'declined', tone: 'negative' }, { col: 'cancelled', tone: 'neutralSoft' }], limit: 10 }],
  // Management (period, as at): pipeline, conversion, approval and speed.
  'team-performance': [
    { type: 'trend', title: 'trend', kind: 'money' },
    { type: 'rows', title: 'teamVolume', form: 'bars', horizontal: true, category: ['team'], series: [{ col: 'submittedVolume', tone: 'progress' }, { col: 'disbursed', tone: 'positiveStrong' }], limit: 10 },
  ],
  'bank-performance': [
    { type: 'trend', title: 'trendCount', kind: 'integer', exclude: ['banks'] },
    { type: 'rows', title: 'bankDecisions', form: 'bars', horizontal: true, category: bank, series: [{ col: 'submitted', tone: 'progress' }, { col: 'decided', tone: 'neutral' }, { col: 'declined', tone: 'negative' }] },
  ],
  'approval-rate': [
    { type: 'trend', title: 'approvalTrend', kind: 'integer' },
    { type: 'rows', title: 'productDecisions', form: 'stacked', horizontal: true, category: ['product'], series: [{ col: 'approved', tone: 'positive' }, { col: 'declined', tone: 'negative' }, { col: 'pending', tone: 'progress' }] },
  ],
  conversion: [
    { type: 'trend', title: 'conversionTrend', kind: 'integer' },
    { type: 'rows', title: 'productConversion', form: 'bars', horizontal: true, category: ['product'], series: [{ col: 'submitted', tone: 'progress' }, { col: 'disbursed', tone: 'positiveStrong' }] },
  ],
  turnaround: [
    { type: 'trend', title: 'turnaroundTrend', kind: 'days', exclude: ['opsHours', 'opsDays'] },
    { type: 'rows', title: 'bankTurnaround', form: 'bars', horizontal: true, category: bank, series: [{ col: 'toDecision', tone: 'progress' }, { col: 'toBooked', tone: 'positive' }, { col: 'toDisbursed', tone: 'positiveStrong' }] },
  ],
  'carry-forward': [{ type: 'trend', title: 'volumeTrend', kind: 'money' }, { type: 'rows', title: 'byBank', form: 'bars', horizontal: true, category: bank, series: [{ col: 'volume', tone: 'neutral' }] }],
  wip: [{ type: 'trend', title: 'volumeTrend', kind: 'money' }, { type: 'rows', title: 'byBank', form: 'bars', horizontal: true, category: bank, series: [{ col: 'volume', tone: 'progress' }] }],
  booked: [{ type: 'trend', title: 'volumeTrend', kind: 'money' }, { type: 'rows', title: 'byBank', form: 'bars', horizontal: true, category: bank, series: [{ col: 'volume', tone: 'positive' }] }],
  disbursed: [{ type: 'trend', title: 'disbursedTrend', kind: 'money' }, { type: 'rows', title: 'byBank', form: 'bars', horizontal: true, category: bank, series: [{ col: 'volume', tone: 'positiveStrong' }] }],
  // Bank revenue (company ledger): earned → invoiced → collected, outstanding and ageing.
  'expected-commission': [{ type: 'trend', title: 'revenueTrend', kind: 'money' }, { type: 'rows', title: 'byBank', form: 'bars', horizontal: true, category: bank, series: [{ col: 'expected', tone: 'neutral' }] }],
  invoiced: [{ type: 'trend', title: 'revenueTrend', kind: 'money' }, { type: 'rows', title: 'invoicedByBank', form: 'stacked', horizontal: true, category: bank, series: [{ col: 'paid', tone: 'positive' }, { col: 'outstanding', tone: 'attention' }] }],
  collected: [{ type: 'trend', title: 'revenueTrend', kind: 'money' }, { type: 'rows', title: 'collectedByBank', form: 'stacked', horizontal: true, category: bank, series: [{ col: 'allocated', tone: 'positive' }, { col: 'unallocated', tone: 'attentionSoft' }] }],
  outstanding: [{ type: 'trend', title: 'revenueTrend', kind: 'money' }, { type: 'rows', title: 'outstandingByBank', form: 'stacked', horizontal: true, category: bank, series: [{ col: 'invoicedOpen', tone: 'attention' }, { col: 'notInvoiced', tone: 'neutralSoft' }] }],
  aging: [{ type: 'rows', title: 'agingByBank', form: 'stacked', horizontal: true, category: bank, series: age(['d0_30', 'd31_60', 'd61_90', 'd90p']) }],
  clawbacks: [{ type: 'trend', title: 'revenueTrend', kind: 'money' }, { type: 'rows', title: 'byBank', form: 'bars', horizontal: true, category: bank, series: [{ col: 'inPeriod', tone: 'negative' }] }],
  'reconciliation-differences': [{ type: 'count', title: 'reconResults', category: 'result', tones: { matched: 'positive', difference: 'attention', missing_at_bank: 'negative', unmatched: 'neutralSoft' } }],
};

/** Figure → tone: meaning, not position. Unknown figures fall back to neutral greys. */
const FIGURE_TONE: Readonly<Record<string, ChartTone>> = {
  submitted: 'progress',
  count: 'progress',
  decided: 'neutral',
  approved: 'positive',
  declined: 'negative',
  disbursed: 'positiveStrong',
  volume: 'progress',
  target: 'neutral',
  expected: 'neutral',
  total: 'neutral',
  invoicedTotal: 'neutral',
  received: 'positive',
  collected: 'positive',
  outstanding: 'attention',
  invoicedOpen: 'attention',
  notInvoiced: 'neutralSoft',
  unallocated: 'attentionSoft',
  inPeriod: 'negative',
  clawedInPeriod: 'negative',
  toDate: 'neutralSoft',
  clawedToDate: 'neutralSoft',
  toDecision: 'progress',
  toBooked: 'positive',
  toDisbursed: 'positiveStrong',
};

export const chartsFor = (key: string): readonly ChartSpec[] => REPORT_CHARTS[key] ?? [];
export const hasTrend = (key: string) => chartsFor(key).some((c) => c.type === 'trend');

const numeric = (v: Schema<'ReportCell'> | undefined): number => (typeof v === 'number' ? v : typeof v === 'string' && /^-?\d+(\.\d+)?$/.test(v) ? Math.abs(Number(v)) : 0);

/** Chart value kind of a report column. */
export function kindOf(c: Pick<Column, 'kind'> | undefined): ChartValueKind {
  if (!c) return 'count';
  return c.kind === 'money' ? 'money' : c.kind === 'percent' ? 'percent' : c.kind === 'days' ? 'days' : 'count';
}

export interface BuiltChart {
  data: ChartDatum[];
  series: ChartSeries[];
  kind: ChartValueKind;
  /** Row index in the result per datum (drill-through). */
  rowIndex: number[];
}

/**
 * Rows → chart data. Series are the spec's columns that the result actually has (in its order), labelled with the
 * column header; category labels come from `label` (codes and statuses translated by the caller).
 */
export function rowsChart(spec: RowsChart, result: Result, label: (column: Column, value: Schema<'ReportCell'>) => string, header: (column: Column) => string): BuiltChart {
  const columns = new Map(result.columns.map((c) => [c.key, c]));
  const present = spec.series.filter((s) => columns.has(s.col));
  const series: ChartSeries[] = present.map((s) => {
    const c = columns.get(s.col);
    return { key: s.col, label: c ? header(c) : s.col, tone: s.tone };
  });
  const first = present[0]?.col;
  let rows = result.rows.map((cells, i) => ({ cells, i }));
  rows = rows.filter((r) => present.some((s) => numeric(r.cells[s.col]) !== 0));
  if (spec.limit && first) rows = [...rows].sort((a, b) => numeric(b.cells[first]) - numeric(a.cells[first])).slice(0, spec.limit);
  const data = rows.map(({ cells, i }) => {
    const catCol = spec.category.find((k) => cells[k] !== null && cells[k] !== undefined && cells[k] !== '');
    const column = catCol ? columns.get(catCol) : undefined;
    const raw = catCol ? cells[catCol] : null;
    return { key: String(i), label: column && raw !== null && raw !== undefined ? label(column, raw) : '—', values: Object.fromEntries(present.map((s) => [s.col, cells[s.col] ?? null])) };
  });
  return { data, series, kind: kindOf(first ? columns.get(first) : undefined), rowIndex: rows.map((r) => r.i) };
}

/** Rows counted per value of a status column (part-to-whole; at most five values, else null → no chart). */
export function countChart(spec: CountChart, result: Result): { key: string; count: number; tone: ChartTone }[] | null {
  if (!result.columns.some((c) => c.key === spec.category)) return null;
  const counts = new Map<string, number>();
  for (const r of result.rows) {
    const v = r[spec.category];
    if (v === null || v === undefined || v === '') continue;
    counts.set(String(v), (counts.get(String(v)) ?? 0) + 1);
  }
  if (!counts.size || counts.size > 5) return null;
  return [...counts.entries()].sort((a, b) => b[1] - a[1]).map(([key, count]) => ({ key, count, tone: spec.tones[key] ?? 'neutral' }));
}

/** Monthly headline figures → trend data (figures of one kind only, so the chart has one value axis). */
export function trendChart(spec: TrendSpec, trend: Schema<'ReportTrend'>, monthLabel: (period: string) => string, figureLabel: (f: Schema<'ReportFigure'>) => string): BuiltChart {
  const exclude = new Set(spec.exclude ?? []);
  const keys: Schema<'ReportFigure'>[] = [];
  for (const p of trend.points) for (const f of p.summary) if (f.kind === spec.kind && !exclude.has(f.key) && !keys.some((k) => k.key === f.key)) keys.push(f);
  const series: ChartSeries[] = keys.slice(0, 4).map((f) => ({ key: f.key, label: figureLabel(f), tone: FIGURE_TONE[f.key] ?? 'neutral' }));
  const data = trend.points.map((p) => ({ key: p.period, label: monthLabel(p.period), values: Object.fromEntries(series.map((s) => [s.key, p.summary.find((f) => f.key === s.key)?.value ?? null])) }));
  return { data, series, kind: spec.kind === 'money' ? 'money' : spec.kind === 'days' ? 'days' : 'count', rowIndex: [] };
}
